import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Ruler, Users, MapPin, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { Product } from '../data/types';
interface ProductCardProps {
  product: Product;
  index?: number;
  onOpenProduct?: () => void;
}
export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  index = 0,
  onOpenProduct
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const allImages = [product.image, ...(product.images || [])];
  const hasMultipleImages = allImages.length > 1;
  const paginate = (dir: number) => {
    setDirection(dir);
    setCurrentImageIndex(prev => (prev + dir + allImages.length) % allImages.length);
  };
  useEffect(() => {
    if (!hasMultipleImages) return;
    const interval = setInterval(() => paginate(1), 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMultipleImages, allImages.length]);
  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    paginate(1);
  };
  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    paginate(-1);
  };
  const currentImage = allImages[currentImageIndex];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'Napuhanci':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Soft Play':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      case 'Sport':
        return 'bg-accent/10 text-yellow-600 border-accent/20';
      case 'Dodaci':
        return 'bg-success/10 text-green-700 border-success/20';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay: index * 0.1
  }} className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col h-full border border-gray-100 group/card" data-id="element-135" data-mp="27r995863i1wt">
      {/* Image */}
      <div className="h-56 w-full bg-gray-100 flex items-center justify-center relative overflow-hidden group" data-id="element-136" data-mp="1p1khpp1gd3m7">
        <AnimatePresence initial={false} custom={direction} data-id="element-137" data-mp="1t3ebddd8d7cv">
          <motion.div key={currentImageIndex} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.25
          }
        }} className="absolute inset-0 flex items-center justify-center" data-id="element-138" data-mp="1f2v8eknmgrhk">
            {currentImage.startsWith('http') || currentImage.startsWith('data:') ? <img src={currentImage} alt={product.name} className="w-full h-full object-cover" data-id="element-139" data-mp="1jdpdna8spars" /> : <span className="text-6xl" data-id="element-140" data-mp="1t53k61z5zauf">{currentImage}</span>}
          </motion.div>
        </AnimatePresence>

        {hasMultipleImages && <>
            <button onClick={prevImage} aria-label="Prethodna slika" className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-white/80 backdrop-blur-sm rounded-full text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white shadow-sm" data-id="element-141" data-mp="2258cd4vse2o0">
              <ChevronLeft size={20} data-id="element-142" data-mp="fj8zpdihqmx" />
            </button>
            <button onClick={nextImage} aria-label="Sljedeća slika" className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-white/80 backdrop-blur-sm rounded-full text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white shadow-sm" data-id="element-143" data-mp="dzrxwk17bk3">
              <ChevronRight size={20} data-id="element-144" data-mp="3dml37cg7h9my" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-145" data-mp="3dn4h8i2qjdvp">
              {allImages.map((_, idx) => <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`} data-id="element-146" data-mp="3mnat5zisc5sr" data-mpi={idx} />)}
            </div>
          </>}

        <div className={`absolute top-4 right-4 z-10 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-bold shadow-sm border ${getCategoryStyle(product.category)}`} data-id="element-147" data-mp="11vqwwi60ra0q">
          {product.category}
        </div>

        {/* Clickable overlay to open product detail */}
        <Link to={`/proizvod/${product.id}`} onClick={onOpenProduct} aria-label={`Pogledaj ${product.name}`} className="absolute inset-0 z-[2]" data-id="element-148" data-mp="2dlhlpmi99hne" />
      </div>

      <div className="p-6 flex flex-col flex-grow" data-id="element-149" data-mp="23pe89khydwl5">
        <Link to={`/proizvod/${product.id}`} onClick={onOpenProduct} data-id="element-150" data-mp="1yprv1b24kgcc">
          <h3 className="text-2xl font-display font-bold text-gray-800 mb-3 hover:text-primary group-hover/card:text-primary transition-colors" data-id="element-151" data-mp="1yrmhitt1ogij">
            {product.name}
          </h3>
        </Link>

        {product.description && <p className="text-gray-600 mb-5 text-sm line-clamp-2 leading-relaxed" data-id="element-152" data-mp="3p2hd8fws3sgd">
            {product.description}
          </p>}

        <div className="space-y-2 mb-6 flex-grow" data-id="element-153" data-mp="3k3e70s52bg6t">
          {product.dimensions && <div className="flex items-center text-sm text-gray-600" data-id="element-154" data-mp="71ac7rz5cana">
              <Ruler size={16} className="mr-2 text-primary" data-id="element-155" data-mp="btbxg3ealbpg" />
              <span data-id="element-156" data-mp="buwnpgtkq8hi">{product.dimensions}</span>
            </div>}
          {product.application && <div className="flex items-center text-sm text-gray-600" data-id="element-157" data-mp="30i26wiga1py">
              <MapPin size={16} className="mr-2 text-secondary" data-id="element-158" data-mp="30qeam6r1303" />
              <span data-id="element-159" data-mp="qr49glkmrhyw">{product.application}</span>
            </div>}
          {product.capacity && <div className="flex items-center text-sm text-gray-600" data-id="element-160" data-mp="uqd4ruvbjnwk">
              <Users size={16} className="mr-2 text-accent" data-id="element-161" data-mp="gser6rotnvi4" />
              <span data-id="element-162" data-mp="gtzouq6fnuk7">Nosivost: {product.capacity}</span>
            </div>}
          {product.ageRestriction && <div className="flex items-center text-sm text-gray-600" data-id="element-163" data-mp="31idu2mr0fedv">
              <Info size={16} className="mr-2 text-red-400" data-id="element-164" data-mp="31k750e8nokro" />
              <span data-id="element-165" data-mp="36i8p92htiq4q">{product.ageRestriction}</span>
            </div>}
        </div>

        <div className="mt-auto pt-5 border-t border-gray-100" data-id="element-166" data-mp="2ywmxww3k0h2m">
          <div className="mb-5" data-id="element-167" data-mp="2tylci4ino5nz">
            <p className="text-sm text-gray-500 mb-1 font-medium" data-id="element-168" data-mp="2txjshajnw8u4">Cijena od</p>
            <p className="text-3xl font-display font-bold text-gray-900" data-id="element-169" data-mp="37yw5pe7ftwcb">
              {product.basePrice}{' '}
              <span className="text-xl text-primary" data-id="element-170" data-mp="32z1cv543qwom">€</span>
            </p>
            <p className="mt-2 text-xs leading-5 text-gray-500" data-id="element-171" data-mp="2f9fg0ob7ctjs">
              Cijena dostave ovisi o lokaciji dostave.
            </p>
          </div>

          <Link to={`/rezervacija?product=${product.id}`} className="block w-full py-4 px-4 bg-primary text-white font-bold text-lg text-center rounded-2xl shadow-[0_4px_0_0_#0891B2] hover:bg-[#0AC2DC] hover:shadow-[0_4px_0_0_#0891B2] active:translate-y-1 active:shadow-[0_0px_0_0_#0891B2] transition-all" data-id="element-172" data-mp="2b9ohfyffp94p">
            Rezerviraj
          </Link>
        </div>
      </div>
    </motion.div>;
};